/**
 * GrantPremiumModal — admin-only modal that calls
 * POST /api/admin/grant-premium with {target_user_id, tier, days, note}.
 *
 * Mirrors the existing GrantVaultModal shape (single dialog, single
 * submit, surfaces server-side errors verbatim). Grants stack — the
 * backend extends the recipient's `premium_until` forward from the
 * later of (now, current expiry) so re-granting never shortens.
 */
import React, { useState } from 'react';
import { Crown, Sparkles, X, Loader2, Check } from 'lucide-react';
import { Button } from './ui/button';
import { authFetch } from '../utils/authUtils';

const TIERS = [
  { value: 'premium', label: 'Premium', hint: 'Vault + premium voices + ad-free' },
  { value: 'premium_top', label: 'Premium+', hint: 'Premium plus higher coin earnings' },
  { value: 'writer_ai_tools', label: 'Scribe AI tools', hint: 'Scribe-only — AI editing + HD TTS' },
];

const QUICK_DAYS = [7, 30, 90, 365];

const GrantPremiumModal = ({ user, onClose, onGranted }) => {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const [tier, setTier] = useState('premium');
  const [days, setDays] = useState(30);
  const [note, setNote] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    const d = Number(days);
    if (!d || d < 1 || d > 3650) {
      setErr('Days must be 1..3650');
      return;
    }
    setBusy(true); setErr('');
    try {
      const r = await authFetch(`${BACKEND_URL}/api/admin/grant-premium`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          target_user_id: user.user_id,
          tier,
          days: d,
          note: note.trim() || undefined,
        }),
      });
      const data = await r.json();
      if (!r.ok) { setErr(data.detail || 'Grant failed'); return; }
      setResult(data);
      onGranted && onGranted();
    } catch {
      setErr('Network error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
      data-testid="grant-premium-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-slate-900 border border-amber-500/40 rounded-2xl w-full max-w-md p-5 space-y-4 relative">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-1 text-slate-400 hover:text-white"
          aria-label="Close"
          data-testid="grant-premium-close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-center gap-2">
          <Crown className="w-5 h-5 text-amber-400" />
          <h2 className="text-white text-lg font-bold">Grant Premium</h2>
        </div>
        <p className="text-xs text-slate-400">
          Target: <span className="text-amber-300 font-semibold">{user.name || user.email}</span>
          <br />Grants stack — extending past the current expiry never shortens.
        </p>

        {!result && (
          <>
            <div>
              <label className="block text-xs uppercase tracking-wider text-slate-400 mb-1.5">Tier</label>
              <div className="flex flex-col gap-1.5">
                {TIERS.map((t) => (
                  <button
                    key={t.value}
                    type="button"
                    onClick={() => setTier(t.value)}
                    disabled={busy}
                    className={`text-left rounded-lg px-3 py-2 border text-sm ${
                      tier === t.value
                        ? 'border-amber-400 bg-amber-500/15 text-amber-100'
                        : 'border-slate-700 bg-slate-800 text-slate-300 hover:border-amber-400/40'
                    }`}
                    data-testid={`grant-premium-tier-${t.value}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-semibold">{t.label}</span>
                      {tier === t.value && <Check className="w-4 h-4 text-amber-300" />}
                    </div>
                    <div className="text-[10px] text-slate-400 mt-0.5">{t.hint}</div>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-xs uppercase tracking-wider text-slate-400 mb-1.5">Duration (days)</label>
              <div className="flex gap-1.5 mb-2 flex-wrap">
                {QUICK_DAYS.map((d) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => setDays(d)}
                    disabled={busy}
                    className={`text-xs font-bold rounded-full px-2.5 py-1 border ${
                      Number(days) === d
                        ? 'bg-amber-500 text-slate-950 border-amber-400'
                        : 'border-slate-700 text-slate-300 hover:border-amber-400/50'
                    }`}
                    data-testid={`grant-premium-days-${d}`}
                  >
                    {d}d
                  </button>
                ))}
              </div>
              <input
                type="number"
                min={1}
                max={3650}
                value={days}
                onChange={(e) => setDays(e.target.value.replace(/\D/g, ''))}
                className="w-full rounded-md bg-slate-800 border border-slate-700 text-white text-sm px-3 py-2 tabular-nums focus:outline-none focus:ring-2 focus:ring-amber-500/40"
                data-testid="grant-premium-days-input"
              />
            </div>

            <div>
              <label className="block text-xs uppercase tracking-wider text-slate-400 mb-1.5">Note (audited)</label>
              <input
                type="text"
                value={note}
                onChange={(e) => setNote(e.target.value.slice(0, 240))}
                placeholder="e.g. comp for the launch issue"
                className="w-full rounded-md bg-slate-800 border border-slate-700 text-white text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500/40"
                data-testid="grant-premium-note"
              />
            </div>

            {err && (
              <div className="text-rose-300 text-xs bg-rose-950/40 border border-rose-500/40 rounded-md px-3 py-2" data-testid="grant-premium-error">
                {err}
              </div>
            )}

            <Button
              onClick={submit}
              disabled={busy}
              className="w-full bg-amber-500 hover:bg-amber-400 disabled:opacity-60 text-slate-950 font-bold"
              data-testid="grant-premium-confirm"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              Grant {tier === 'premium_top' ? 'Premium+' : tier === 'writer_ai_tools' ? 'Scribe AI tools' : 'Premium'} · {days}d
            </Button>
          </>
        )}

        {result && (
          <div className="rounded-xl border border-emerald-500/40 bg-emerald-950/30 p-4 text-sm text-emerald-100 space-y-1" data-testid="grant-premium-success">
            <div className="flex items-center gap-2 font-bold">
              <Check className="w-4 h-4" /> Granted
            </div>
            <div>Tier: <span className="font-semibold">{result.tier}</span></div>
            <div>Days added: <span className="font-semibold">{result.days}</span></div>
            <div>Expires: <span className="font-mono text-xs">{String(result.premium_until).slice(0, 19)} UTC</span></div>
            <Button onClick={onClose} variant="outline" size="sm" className="mt-2 w-full text-white border-emerald-500/40">
              Close
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default GrantPremiumModal;
